import { initializeExperiments } from "@func-core/core/application/services/experiment-service";
import { onClientEntryAdapter } from "@func-core/core/data/adapters";
import { usePromotionCodeStore, useResultStore } from "@func-core/core/data/repositories";
import { useCountryStore } from "@func-core/core/data/repositories/country-detection-repository";
import { AVAILABLE_COUNTRIES, DEFAULT_LOCALE } from "@func-core/core/domain/constants";
import {
  CountryDetectionResponse,
  CountrySource,
  CountrySourceType,
  createCountryViewModel,
} from "@func-core/core/domain/view-models";
import { PartialWindow } from "@func-core/core/types";

const initializeApplicationStores = (): void => {
  usePromotionCodeStore.getState().initializeFromStorage();
};

const initializeCountryAndExperiments = (
  countryIso: string,
  source: CountrySourceType,
  leadEmail?: string
) => {
  const countryStore = useCountryStore.getState();
  const country = AVAILABLE_COUNTRIES[countryIso];

  const detection: CountryDetectionResponse = {
    locale: country.language.code,
    source,
  };

  const countryViewModel = createCountryViewModel(detection);

  countryStore.setCountryState(countryViewModel.country, countryViewModel.source);

  initializeExperiments({ country: countryViewModel.country, leadEmail });

  return true;
};

export const onClientEntryService = async (windowObj = window as PartialWindow): Promise<void> => {
  initializeApplicationStores();

  // TODO: Refactor the country detection logic below using Strategy pattern
  const { isResultUrl, resultUuid, countryIsoParam, urlLocale } = onClientEntryAdapter(windowObj);

  if (isResultUrl && resultUuid) {
    const fetchResult = useResultStore.getState().fetchResult;
    const result = await fetchResult(resultUuid);

    if (result?.country && result.email) {
      initializeCountryAndExperiments(result.country, CountrySource.RESULT, result.email);

      return;
    }
  }

  if (countryIsoParam) {
    initializeCountryAndExperiments(countryIsoParam, CountrySource.URL);

    return;
  }

  if (urlLocale) {
    const matchingCountry = Object.entries(AVAILABLE_COUNTRIES).find(
      ([, country]) => country.language.code.toLowerCase() === urlLocale.toLowerCase()
    );

    if (matchingCountry) {
      const [countryIso] = matchingCountry;
      initializeCountryAndExperiments(countryIso, CountrySource.URL);

      return;
    }
  }

  const defaultCountry = Object.entries(AVAILABLE_COUNTRIES).find(
    ([, country]) => country.language.code === DEFAULT_LOCALE.code
  );

  if (defaultCountry) {
    const [defaultCountryIso] = defaultCountry;
    initializeCountryAndExperiments(defaultCountryIso, CountrySource.DEFAULT);
  }
};
